import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import { withRouter } from 'react-router';
import { Redirect } from 'react-router-dom';
import Helmet from 'helmet/helmet';
import { URLBuilder } from 'url-builder/url-builder';
import { IntlMessage } from 'react-intl/intl-message';
import lock from 'four-nets/icons/svg/icon_lock.svg';

import SVGIcon from 'lib/images/svg-icon/svg-icon';
import Localizable from 'lib/intl/localizable';
import Styled from 'styled';
import { URLBuilderLink } from 'lib/url/url-builder-link';
import withDialog from 'lib/alerts/with-dialog';
import { DeleteDialog, InfoDialog } from 'lib/alerts/dialog';
import { Halfpage, ZoznamHalfpageTop, ZoznamHalfpageBottom } from 'lib/google/dfp/ad-units/halfpage';
import { Targetting } from 'lib/google/dfp/google-dfp';

import { RedirectError } from 'four-nets/router/errors';
import TitleView from 'four-nets/ui/title-view';
import SearchPanel from 'four-nets/search/search-panel/search-panel';
import SplitView from 'four-nets/ui/layout/split-view';
import BreadcrumbsView, { BreadcrumbsViewModel } from 'four-nets/ui/breadcrumbs-view';
import UserGridListStackView from 'four-nets/user/user-grid-list-stack-view';
import NewCommentComplaintView, {
    NewCommentComplaintViewModel,
} from 'four-nets/common/comment/new-comment-complaint-view';
import EditCommentView, { EditCommentViewModel } from 'four-nets/common/comment/edit-comment-view';
import EditThreadView, { EditThreadViewModel } from 'four-nets/forum/thread/edit-thread/edit-thread-view';
import DeleteCommentView, { DeleteCommentViewModel } from 'four-nets/common/comment/delete-comment-view';
import MostPopularPbAlbums from 'four-nets/widgets/most-popular-pb-albums/most-popular-pb-albums';
import ComposeMailDialog from 'four-nets/mails/compose-mail/compose-mail-dialog';
import PrArticles from 'four-nets/widgets/pr-articles/pr-articles';
import FeaturedContent from 'four-nets/featured-content/featured-content';
import ThreadVendorsPanel from 'four-nets/content-pairing/forum-vendors/thread-vendors-panel/thread-vendors-panel';
import RelatedThreads from 'four-nets/widgets/related-threads/related-threads';
import RelatedAlbums from 'four-nets/forum/related-content/related-albums';
import RelatedInspirations from 'four-nets/forum/related-content/related-inspirations';
import RelatedMagazinePosts from 'four-nets/forum/related-content/related-magazine-posts';

import FirstMessageView from '../message/first-message-view';
import { MessageViewModel } from '../message/message-view';
import { NewMessageViewModel } from '../message/new-message-view';
import { CategoryLink } from '../category-link';
import ThreadStatusBarView, { ThreadStatusBarViewModel } from './thread-status-bar/thread-status-bar-view';
import ThreadMessagesLoader from './thread-messages-loader';


import defaultStyles from './thread-view.scss';

const addTrailingSlash = value => {
    if (value.endsWith('/')) {
        return value;
    }
    return `${value}/`;
};

export class ThreadViewModel {
    thread: Thread;
    breadcrumbsViewModel: BreadcrumbsViewModel;
    threadStatusBarViewModel: ThreadStatusBarViewModel;

    @observable firstMessageViewModel: MessageViewModel;

    constructor(dataSource, params) {
        this.dataSource = dataSource;
        this.params = params;
    }

    loadDidSucceed = ([thread, category]) => {
        thread.category = category;
        this.thread = thread;

        this.breadcrumbsViewModel = new BreadcrumbsViewModel({
            links: [
                new URLBuilderLink({
                    url: 'forum',
                    label: defineMessages({ FORUM_LINK_LABEL: { id: 'FORUM_LINK_LABEL' } }).FORUM_LINK_LABEL,
                }),
                new CategoryLink({ category: thread.category }),
            ],
        });

        this.newMessageViewModel = new NewMessageViewModel({
            thread,
        });

        this.firstMessageViewModel = new MessageViewModel({
            urlBuilder: URLBuilder.defaultURLBuilder,
            mailStore: this.dataSource.mailStore,
            message: thread.firstMessage,
            highlight: true,
            userMentionable: this.newMessageViewModel,
            userStore: this.dataSource.userStore,
            categoryStore: this.dataSource.categoryStore,
        });

        this.threadStatusBarViewModel = new ThreadStatusBarViewModel({
            thread,
        });

        this.editThreadViewModel = new EditThreadViewModel({
            thread,
            categoryStore: this.dataSource.categoryStore,
        });
    };

    load() {
        return Promise.all([
            this.dataSource.threadStore.loadSlug({ slug: this.params.slug }),
            this.dataSource.categoryStore.loadSlug({ slug: this.params.category }),
        ])
            .then(result => {
                const [thread, category] = result;

                if (!thread || !category) {
                    return Promise.reject(
                        new RedirectError(
                            URLBuilder.defaultURLBuilder.getUrl({
                                url: 'forum',
                            })
                        )
                    );
                }

                const [, slug] = thread.url.split('/').reverse();

                if (slug !== this.params.slug) {
                    return Promise.reject(new RedirectError(thread.url));
                }

                return result;
            })
            .then(this.loadDidSucceed);
    }

    fromJSON({ thread, category }) {
        this.loadDidSucceed([
            this.dataSource.threadStore.processJSON({ json: thread }),
            this.dataSource.categoryStore.processJSON({ json: category }),
        ]);
    }

    toJSON() {
        return {
            thread: this.thread,
            category: this.thread.category,
        };
    }
}

@Localizable()
@withDialog
@Styled(defaultStyles)
@observer
class ThreadView extends React.Component {
    static contextTypes = {
        site: PropTypes.object,
    };

    static childContextTypes = {
        showLikers: PropTypes.func,
        writeMailToAuthor: PropTypes.func,
        complain: PropTypes.func,
        edit: PropTypes.func,
        delete: PropTypes.func,
        threadViewModel: PropTypes.object,
    };

    state = { open: false, userGridListViewModel: null, composeMailOpen: false, to: [], subject: '' };

    getChildContext() {
        return {
            threadViewModel: this.props.threadViewModel,
            showLikers: this.showMessageLikers,
            writeMailToAuthor: this.writeMailToMessageAuthor,
            complain: this.complain,
            edit: this.editMessage,
            delete: this.deleteMessage,
        };
    }

    showMessageLikers = userGridListViewModel => {
        this.setState({ userGridListViewModel, open: true });
    };

    closeComposeMail = () => {
        this.setState({ composeMailOpen: false });
    };

    writeMailToMessageAuthor = message => {
        this.setState({
            to: [message.user.username],
            subject: message.mailSubject({ intl: this.props.intl }),
            composeMailOpen: true,
        });
    };

    newCommentComplaintView = null;
    setNewCommentComplaintView = ref => {
        this.newCommentComplaintView = ref;
    };

    complain = message => {
        if (this.newCommentComplaintView) {
            const newCommentComplaintViewModel = new NewCommentComplaintViewModel({
                comment: message,
            });
            this.newCommentComplaintView.show({
                form: newCommentComplaintViewModel.setupForm(),
            });
        }
    };

    editMessageView = null;
    setEditMessageView = ref => {
        this.editMessageView = ref;
    };

    editThreadView = null;
    setEditThreadView = ref => {
        this.editThreadView = ref;
    };

    editMessage = message => {
        // editing thread
        if (message.isFirstMessage) {
            // #FIXME: What to do when EditThreadViewModel is undefined?
            this.editThreadView.show({
                form: this.props.threadViewModel.editThreadViewModel.setupForm(),
            });
            // editing message
        } else {
            const editCommentViewModel = new EditCommentViewModel({ comment: message });
            this.editMessageView.show({
                form: editCommentViewModel.setupForm(),
            });
        }
    };

    deleteMessageView = null;
    setDeleteMessageView = ref => {
        this.deleteMessageView = ref;
    };

    deleteMessage = message => {
        if (message.isFirstMessage) {
            this.props.showDialog(
                new DeleteDialog({
                    del: () => {
                        message
                            .deleteThread()
                            .catch(err => {
                                this.props.showDialog(
                                    new InfoDialog({
                                        message: new IntlMessage({
                                            message: defineMessages({
                                                FORUM_THREAD_DELETE_ERROR_TEXT: {
                                                    id: 'FORUM_THREAD_DELETE_ERROR_TEXT',
                                                },
                                            }).FORUM_THREAD_DELETE_ERROR_TEXT,
                                        }),
                                    })
                                );
                            })
                            .then(() => {
                                this.props.showDialog(
                                    new InfoDialog({
                                        onOK: () => {
                                            const category = message.thread.url.split('/').reverse()[2];
                                            this.props.history.push(`/forum/category/${category}/`);
                                        },
                                        message: new IntlMessage({
                                            message: defineMessages({
                                                FORUM_THREAD_DELTETE_SUCCESS: { id: 'FORUM_THREAD_DELTETE_SUCCESS' },
                                            }).FORUM_THREAD_DELTETE_SUCCESS,
                                        }),
                                    })
                                );
                            });
                    },
                    message: new IntlMessage({
                        message: defineMessages({
                            FORUM_THREAD_DELETE_CONFIRMATION_TEXT: { id: 'FORUM_THREAD_DELETE_CONFIRMATION_TEXT' },
                        }).FORUM_THREAD_DELETE_CONFIRMATION_TEXT,
                    }),
                })
            );
        } else {
            const deleteMessageViewModel = new DeleteCommentViewModel({
                comment: message,
            });
            this.deleteMessageView.show({
                form: deleteMessageViewModel.setupForm(),
            });
        }
    };

    renderHelmet = (H: React.ComponentClass) => {
        const { site } = this.context;
        const { thread } = this.props.threadViewModel;

        const title = `${thread.title} - ${thread.category.name} | ${site.name}`;

        return (
            <H>
                <title>{title}</title>,
                <meta property='og:title' content={title} />
            </H>
        );
    };

    render() {
        const {
            page,
            desc,
            post,
            threadViewModel: {
                params,
                thread,
                threadStatusBarViewModel,
                breadcrumbsViewModel,
                firstMessageViewModel,
                editThreadViewModel,
            },
            styles,
        } = this.props;
        const { site } = this.context;
        const [, slug] = thread.url.split('/').reverse();
        const [, locationSlug] =
            (this.props.location.pathname && addTrailingSlash(this.props.location.pathname).split('/').reverse()) || [];

        if (addTrailingSlash(slug) !== addTrailingSlash(params.slug)) {
            return <Redirect to={thread.url} />;
        }

        const mainContent = (
            <div className={styles.main}>
                <Helmet render={this.renderHelmet} />
                <div className={styles.wrapper}>
                    <TitleView
                        title={
                            <h1 className={styles.heading}>
                                {thread.title}
                                {thread.isLocked ? <SVGIcon style={{ image: styles.icon }} src={lock} /> : null}
                            </h1>
                        }
                    />
                    <BreadcrumbsView breadcrumbsViewModel={breadcrumbsViewModel} />
                    <div className={styles.threadStatusBar}>
                        <ThreadStatusBarView threadStatusBarViewModel={threadStatusBarViewModel} />
                        <div key='firstMessage' className={styles.firstMessage}>
                            <FirstMessageView
                                key={firstMessageViewModel.message.id}
                                messageViewModel={firstMessageViewModel}
                            />
                        </div>
                        {addTrailingSlash(locationSlug) === addTrailingSlash(slug) ? (
                            <ThreadMessagesLoader
                                threadViewModel={this.props.threadViewModel}
                                page={page}
                                desc={desc}
                                post={post}
                            />
                        ) : null}
                    </div>
                </div>
                <div>
                    <TitleView
                        title={
                            <h2 className={styles.searchHeading}>
                                <FormattedMessage id='DIDNT_FIND_WHAT_YOU_LOOKING_FOR' />
                            </h2>
                        }
                    />
                    <SearchPanel style={{ searchPanel: styles.searchPanel }} target='forum' submitOnToggle={false} />
                </div>
                {/* Following components are used from messages and are here to improve
                    rendering perfomance.  These are pop up stackViews which can be
                    shared between thread's messages and don't have to be "bundled"
                    with each message separately. */}
                <UserGridListStackView
                    open={Boolean(this.state.userGridListViewModel && this.state.open)}
                    onClose={() => {
                        this.setState({ open: false });
                    }}
                    title={<FormattedMessage id='LIKE' />}
                    userGridListViewModel={this.state.userGridListViewModel}
                />
                <ComposeMailDialog
                    open={this.state.composeMailOpen}
                    to={this.state.to}
                    subject={this.state.subject}
                    onClose={this.closeComposeMail}
                />
                <NewCommentComplaintView innerRef={this.setNewCommentComplaintView} />
                <EditCommentView innerRef={this.setEditMessageView} />
                <EditThreadView innerRef={this.setEditThreadView} editThreadViewModel={editThreadViewModel} />
                <DeleteCommentView innerRef={this.setDeleteMessageView} />
            </div>
        );

        const targettings = [new Targetting('f-rectangle-cat-slug', [thread.category.slug])];
        const sideContent = (
            <div className={styles.side}>
                <RelatedThreads threadId={thread.id} threadTitle={thread.title} threadSlug={thread.category.slug} />
                <ThreadVendorsPanel threadCategoryId={thread.category.id} />
                <FeaturedContent />
                {site.isSlovak && site.isWedding ? (
                    <ZoznamHalfpageTop targettings={targettings} />
                ) : (
                    <Halfpage targettings={targettings} adUnitCode='halfpage-top' />
                )}
                <MostPopularPbAlbums />
                <PrArticles />
                {site.isSlovak && site.isWedding ? (
                    <ZoznamHalfpageBottom wrapperStyle={{ position: 'sticky', top: 80 }} />
                ) : (
                    <Halfpage
                        wrapperStyle={{ position: 'sticky', top: 80 }}
                        targettings={targettings}
                        adUnitCode='halfpage-bottom'
                    />
                )}
            </div>
        );

        return (
            <div className={styles.outterWrapper}>
                <SplitView mainContent={mainContent} sideContent={sideContent} />
                {site.isLiving ? null : <RelatedMagazinePosts category={thread.category} />}
                <RelatedAlbums category={thread.category} />
                <RelatedInspirations category={thread.category} />
            </div>
        );
    }
}

export default withRouter(ThreadView);
